import React from "react";
import { cn } from "../../lib/cn";

export type NavLinkProps = {
  label: React.ReactNode;
  to: string;
  /**
   * Current pathname from router (e.g., "/clients"). Used to compute active state.
   */
  pathname: string;
  /**
   * Hash prefix for HashRouter links. Defaults to "#".
   */
  hashPrefix?: string;
  /**
   * Optional leading icon.
   */
  leftIcon?: React.ReactNode;
  /**
   * Optional click handler (e.g., to close mobile navigation).
   */
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  /**
   * Optional custom className.
   */
  className?: string;
};

function isActive(pathname: string, to: string): boolean {
  if (to === "/") return pathname === "/";
  return (
    pathname === to || pathname.startsWith(`${to}/`) || pathname.startsWith(to)
  );
}

/**
 * HashRouter-friendly navigation link that renders an <a> with the proper hash prefix.
 * Keeps styling token-driven and reusable for Sidebar / Topbar navigation.
 */
export function NavLink({
  label,
  to,
  pathname,
  hashPrefix = "#",
  leftIcon,
  onClick,
  className,
}: NavLinkProps) {
  const active = isActive(pathname, to);

  return (
    <a
      href={`${hashPrefix}${to}`}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={cn(
        "group flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition-colors",
        active
          ? [
              "bg-[color:var(--kkp-surface-2)]",
              "text-[color:var(--kkp-text)]",
              "border border-[color:var(--kkp-border)]",
              "shadow-[0_1px_0_rgba(255,255,255,0.06),0_10px_20px_rgba(0,0,0,0.18)]",
              "ring-1 ring-[color:color-mix(in_oklab,var(--kkp-purple)_28%,transparent)]",
            ].join(" ")
          : [
              "border border-transparent",
              "text-[color:var(--kkp-text-muted)]",
              "hover:text-[color:var(--kkp-text)]",
              "hover:bg-[color:var(--kkp-surface)]",
              "hover:border-[color:var(--kkp-border)]",
              "hover:ring-1 hover:ring-[color:color-mix(in_oklab,var(--kkp-purple)_22%,transparent)]",
            ].join(" "),
        className,
      )}
    >
      {leftIcon ? (
        <span
          className={cn(
            "inline-flex shrink-0 transition-colors",
            active
              ? "text-[color:var(--kkp-purple)]"
              : "text-[color:color-mix(in_oklab,var(--kkp-text-muted)_85%,white)] group-hover:text-[color:var(--kkp-purple)]",
          )}
        >
          {leftIcon}
        </span>
      ) : null}
      <span className="truncate">{label}</span>
      {active ? (
        <span className="ml-auto h-2 w-2 rounded-full bg-[color:var(--kkp-purple)]" />
      ) : null}
    </a>
  );
}
